import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NWClient } from '../../../client/NWClient';
import { DocumentHtmlDataModel } from '../../../models/document.html.data.model';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { sanitizeRedirectUrl } from '../../../common/helpers/sanitize';

export const useDocumentHTML = (
  htmlArray?: number[],
  allowGettingHTML?: boolean,
  documentGeneratedHTML?: string
) => {
  const { t } = useTranslation();
  const { documentId } = useParams();
  const currentUser = useAppSelector(selectUser);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const contractGenerated = params.get('contractGenerated');
  const editDocumentParam = params.get('editDocument');
  const backURL = params.get('backURL');
  const [documentHTML, setDocumentHTML] = useState<string>('');
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [contentEditable, setContentEditable] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const saveDocumentHTML = () => {
    setPendingStatus(true);
    if (contractGenerated) {
      const data = {
        user: currentUser.id,
        html: documentHTML,
      };
      NWClient.patch(`document-contract/${documentId}/`, null, data)
        .then(() => {
          toast.success(t('messages.documentUpdated'));
          setContentEditable(false);
          navigate(
            window.location.pathname +
              '?initialTab=0&contractGenerated=true' +
              (backURL ? '&backURL=' + encodeURIComponent(sanitizeRedirectUrl(backURL)) : '')
          );
        })
        .catch((error) => {
          toast.error(error.message || t('messages.errorOccurred'));
        })
        .finally(() => setPendingStatus(false));
    } else {
      const data = { html: documentHTML, document_upload: Number(documentId) };
      NWClient.post('document-upload-html', data)
        .then(() => {
          localStorage.setItem('firstConversionFail', 'false');
          toast.success(t('messages.documentContentSaved'));
          setContentEditable(false);
          navigate(window.location.pathname + '?initialTab=0');
        })
        .catch((error) => {
          if (localStorage.getItem('firstConversionFail') === 'true') {
            toast.error(error.message || t('messages.errorOccurred'));
          } else {
            localStorage.setItem('firstConversionFail', 'true');
            toast.error(t('messages.firstDocumentConversionFail'));
          }
        })
        .finally(() => setPendingStatus(false));
    }
  };

  const fetchDocumentHTML = useCallback(() => {
    if (
      !contractGenerated &&
      htmlArray &&
      htmlArray.length > 0 &&
      allowGettingHTML &&
      !documentHTML
    ) {
      const htmlId = localStorage.getItem('doc_latest_version')
        ? Math.max(...htmlArray)
        : Math.min(...htmlArray);

      NWClient.get('document-upload-html-data', htmlId)
        .then((res: DocumentHtmlDataModel) => {
          setDocumentHTML(res.html);
        })
        .catch((error) => {
          if (error.response && error.response.status !== 404) {
            toast.error(error.message || t('messages.errorOccurred'));
          }
        })
        .finally(() => setPendingStatus(false));
    }
  }, [contractGenerated, htmlArray, allowGettingHTML, documentHTML, t]);

  useEffect(() => {
    if (documentGeneratedHTML) {
      setDocumentHTML(documentGeneratedHTML);
    }
  }, [documentGeneratedHTML]);

  useEffect(() => {
    fetchDocumentHTML();
  }, [fetchDocumentHTML]);

  useEffect(() => {
    if (editDocumentParam) {
      setContentEditable(true);
    }
  }, [editDocumentParam]);

  return {
    documentHTML,
    setDocumentHTML,
    pendingStatus,
    saveDocumentHTML,
    contentEditable,
    setContentEditable,
    showConfirmationModal,
    setShowConfirmationModal,
  };
};
